"use client";

import { MegaMenuSectionStoryblok } from "@/generated/sb/sb-types";
import { Link, usePathname } from "@/i18n/routing";
import { getLink, StoryblokComponent } from "@frend-digital/storyblok";
import clsx from "clsx";
import { Dispatch, SetStateAction } from "react";
import MobileMenuModal from "./Mobile/MobileMenuModal/MobileMenuModal";
import styles from "./index.module.css";

interface MenuSectionLinkType {
  link: MegaMenuSectionStoryblok;
  open?: boolean;
  setActiveSection?: Dispatch<SetStateAction<string>>;
}

const addHex = (hex: string) => {
  return hex.startsWith("#") ? hex : `#${hex}`;
};

export const MenuSectionLink = ({
  link,
  open,
  setActiveSection,
}: MenuSectionLinkType) => {
  const href = getLink(link.link)?.href;

  const handleSetActiveSection = () => setActiveSection?.(link?._uid);
  const pathname = usePathname();
  return (
    <li>
      <span
        style={
          {
            "--menu-link-color": link.textColor
              ? addHex(link.textColor)
              : undefined,
          } as React.CSSProperties
        }
        className={clsx(
          styles.menuSectionLink,
          open && styles.open,
          styles.desktopModal,
          (pathname === href || pathname + "/" === href) && styles.current,
        )}
        onMouseEnter={handleSetActiveSection}
        onMouseLeave={() => setActiveSection && setActiveSection("")}
      >
        <Link className={styles.menuLink} href={href ?? ""}>
          {link.text}
        </Link>
      </span>
      <span className={styles.mobileModal}>
        <MobileMenuModal link={link} />
      </span>
    </li>
  );
};

const FirstSection = ({ link }: IMenuSectionType) => {
  const { links } = link;

  return (
    <div className={styles.desktopMenu__column}>
      <ul className={styles.desktopMenu__sectionLinks}>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {links?.map((link: any) => (
          <MenuSectionLink link={link} key={link._uid} />
        ))}
      </ul>
    </div>
  );
};

export interface IMenuSectionType {
  link: MegaMenuSectionStoryblok;
  active?: boolean;
  setActiveSection?: Dispatch<SetStateAction<string>>;
  preview: boolean;
}

export const MenuSectionItem = ({
  link,
  setActiveSection,
  preview,
}: IMenuSectionType) => {
  const { links, card } = link;
  const handleSetActiveSection = () => setActiveSection?.(link._uid);
  if (!links?.length) {
    return null;
  }

  return (
    <div
      className={styles.desktopMenu__wrap}
      data-layout="page"
      onMouseEnter={handleSetActiveSection}
    >
      <FirstSection link={link} preview={preview} />

      {card && card.length > 0 && (
        <div className={styles.desktopMenu__bloks}>
          {card.map(item => (
            <StoryblokComponent blok={item} key={item._uid} preview={preview} />
          ))}
        </div>
      )}
    </div>
  );
};

export const MenuSectionModal = ({
  children,
  open,
  setActiveSection,
}: {
  children: React.ReactNode;
  open: boolean;
  setActiveSection: Dispatch<SetStateAction<string>>;
}) => {
  const menuSectionModalStyles = `${open ? styles.active : ""} ${
    open ? "active" : ""
  } ${styles.desktopMenu__section} menu-section`;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const a11yProps = open ? {} : ({ "aria-hidden": true, inert: true } as any);

  return (
    <div
      {...a11yProps}
      onMouseLeave={() => setActiveSection?.("")}
      className={menuSectionModalStyles}
    >
      {children}
    </div>
  );
};
